/**
 * Reads the payload contract v2 (#1089) out of a notification's `data`.
 *
 * `data` is stored as free JSON and rows written before the contract carry
 * none of its keys, or carry an unrelated `category` (a bug report's "ui").
 * Everything here therefore reads defensively: a malformed value counts as
 * absent, never as an error.
 */
import { APPS, RETIRED_APP_IDS } from '@/data/apps'
import { resolveDeepLinkAppId } from '@/services/deep-link-service'
import type {
  Notification,
  NotificationActor,
  NotificationCategory,
  NotificationOutcome,
  NotificationType,
} from '@/services/notification-service'
import type { WindowParams } from '@/types/window'

const CATEGORIES: readonly NotificationCategory[] = ['bug_reports', 'documents', 'routines', 'requests', 'shared']
const OUTCOMES: readonly NotificationOutcome[] = ['success', 'failure', 'info', 'action_required']

/** Category of rows that do not name one, by type. */
const CATEGORY_BY_TYPE: Partial<Record<NotificationType, NotificationCategory>> = {
  new_bug_report: 'bug_reports',
  bug_report_reply: 'bug_reports',
  bug_report_status_changed: 'bug_reports',
  bug_report_type_changed: 'bug_reports',
  document_upload_completed: 'documents',
  document_upload_failed: 'documents',
  document_processing_completed: 'documents',
  document_processing_failed: 'documents',
  recording_transcription_completed: 'documents',
  recording_transcription_failed: 'documents',
  review_tasks_created: 'documents',
  routine_completed: 'routines',
  routine_failed: 'routines',
  access_request: 'requests',
  new_feature_request: 'requests',
  quota_request: 'requests',
  quota_request_decided: 'requests',
  session_assigned: 'shared',
  prompt_shared: 'shared',
}

/** Outcome of rows that do not name one, by type; anything else is `info`. */
const OUTCOME_BY_TYPE: Partial<Record<NotificationType, NotificationOutcome>> = {
  document_upload_completed: 'success',
  document_processing_completed: 'success',
  recording_transcription_completed: 'success',
  routine_completed: 'success',
  user_approved: 'success',
  document_upload_failed: 'failure',
  document_processing_failed: 'failure',
  recording_transcription_failed: 'failure',
  routine_failed: 'failure',
  user_rejected: 'failure',
  access_request: 'action_required',
  quota_request: 'action_required',
  review_tasks_created: 'action_required',
}

/**
 * Where rows without a `target` lead, by type. Routine runs were clickable
 * before the contract (their answer lives in the routine's conversation in
 * Communicator 2.0); keep that for rows cdb_tqs writes without a target.
 */
const LEGACY_TARGET_BY_TYPE: Partial<Record<NotificationType, ResolvedNotificationTarget>> = {
  routine_completed: { appId: 'communicator2', params: { view: 'routines' } },
  routine_failed: { appId: 'communicator2', params: { view: 'routines' } },
}

export interface ResolvedNotificationTarget {
  appId: string
  params?: WindowParams
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

function isOpenableApp(appId: string): boolean {
  return !RETIRED_APP_IDS.has(appId) && APPS.some((app) => app.id === appId)
}

/** Primitive params only (window params cannot hold objects); others are dropped. */
function primitiveParams(value: unknown): WindowParams | undefined {
  if (!isRecord(value)) return undefined
  const params: WindowParams = {}
  for (const [key, raw] of Object.entries(value)) {
    if (typeof raw === 'string' || typeof raw === 'boolean' || (typeof raw === 'number' && Number.isFinite(raw))) {
      params[key] = raw
    }
  }
  return Object.keys(params).length > 0 ? params : undefined
}

/**
 * Quota requests written before cdb_webos#1241 point at Cortex → Token-Nutzung
 * without naming the queue, which opened "Übersicht". The request id is in
 * `data` anyway, so such a target is completed here — the "Prüfen" button of a
 * stored notification then lands on "Anfragen" too, with the request in focus.
 */
function withQuotaRequestFocus(
  notification: Notification,
  target: ResolvedNotificationTarget,
): ResolvedNotificationTarget {
  if (notification.type !== 'quota_request' || target.appId !== 'cortex') return target
  if (target.params?.tab !== 'token-usage' || target.params.subtab) return target
  const requestId = nonEmptyString(notification.data?.request_id)
  return {
    appId: target.appId,
    params: { ...target.params, subtab: 'requests', ...(requestId ? { requestId } : {}) },
  }
}

/**
 * The window a click on the notification opens, or `null` when it leads
 * nowhere. An explicit `data.target` wins; it must name an app this desktop
 * knows. Rows without one fall back to the legacy mapping by type.
 */
export function resolveNotificationTarget(notification: Notification): ResolvedNotificationTarget | null {
  const target = notification.data?.target as unknown
  if (isRecord(target)) {
    const app = nonEmptyString(target.app)
    const appId = app ? resolveDeepLinkAppId(app) : undefined
    if (appId && isOpenableApp(appId)) {
      const params = primitiveParams(target.params)
      return withQuotaRequestFocus(notification, params ? { appId, params } : { appId })
    }
  }
  return LEGACY_TARGET_BY_TYPE[notification.type] ?? null
}

export function notificationCategory(notification: Notification): NotificationCategory | undefined {
  const category = notification.data?.category as unknown
  if (CATEGORIES.includes(category as NotificationCategory)) return category as NotificationCategory
  return CATEGORY_BY_TYPE[notification.type]
}

export function notificationOutcome(notification: Notification): NotificationOutcome {
  const outcome = notification.data?.outcome as unknown
  if (OUTCOMES.includes(outcome as NotificationOutcome)) return outcome as NotificationOutcome
  return OUTCOME_BY_TYPE[notification.type] ?? 'info'
}

export function notificationGroupKey(notification: Notification): string | undefined {
  return nonEmptyString(notification.data?.group_key)
}

export function notificationActor(notification: Notification): NotificationActor | undefined {
  const actor = notification.data?.actor as unknown
  if (!isRecord(actor)) return undefined
  const user_id = nonEmptyString(actor.user_id)
  const name = nonEmptyString(actor.name)
  if (!user_id && !name) return undefined
  return { ...(user_id ? { user_id } : {}), ...(name ? { name } : {}) }
}
